/* mm-pdp: product page runtime for the PDP sections (loaded deferred by each of them; every element is defined once).
   <mm-pdp>             sections/mm-pdp-buy-box: gallery, options, size links, quantity, add to cart, sticky bar,
                        phone colour sheet. Talks to <mm-bundle-picker> (assets/mm-bundle-picker.js).
   <mm-recommendations> sections/mm-recommendations: Shopify product recommendations (complementary → related).
   (<mm-ship-by>, the ships-by / delivery estimate, is shared from assets/mm-global.js since R2.)
   <mm-acc-group>       sections/mm-pdp-details + mm-faq: opens the accordion selected in the theme editor; mounts
                        the global shipping rates (template from sections/mm-shipping-rates) into shipping accordions.
   Design v5 (2026-10-23): strikes are compare-at × bags (the variant's compare-at, or its price when it has none);
   totals for a bundle × the quantity stepper are the engine's price for all those bags.
   Add to cart (BUILD-CONVENTIONS §4, same payload as live):
     no tier → MM.addItems([{ id: selected variant id, quantity: quantity selector }])
     tier    → <mm-bundle-picker>.add(btn, err, quantity) → [{ id, quantity: bags of that colour × quantity }] per
               chosen colour (1 bag = the page's variant), no properties.
   Colour card (sheet): sticky-bar Add on colour-only products opens it (data-sheet-phone / data-sheet-desktop); a tap
   sets the colour of the active bag (1 bag: the page's variant), then Add to cart · total adds as above.
   Reels ([data-reel] videos in the buy box) play muted while on screen. */
(() => {
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const E = () => window.MaisonEngine;
  const money = (c) => (window.MM?.money || E()?.money || ((x) => `$${(x / 100).toFixed(2)}`))(c);
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const fill = (tpl, map) => String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
  const clean = (name) => String(name || '').replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const sized = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  const phone = () => window.matchMedia('(max-width: 759px)').matches;
  const isPre = (v) => /pre-?order/i.test(v?.title || '');
  const preNote = (v) => { const m = (v?.title || '').match(/\(\s*pre-?order\s*\|?\s*([^)]*)\)/i); return m ? (m[1].trim() || '') : null; };

  /* ---------------- Buy box ---------------- */
  define('mm-pdp', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const json = this.querySelector('script[data-mm-product]');
      if (!json) return;
      this.ready = true;
      this.product = JSON.parse(json.textContent);
      this.input = this.querySelector('[data-variant-input]');
      this.variant = this.product.variants.find((v) => v.id === Number(this.input?.value)) || this.product.variants[0];
      this.selected = [...(this.variant?.options || [])];
      this.qty = 1;
      this.qtyInput = this.querySelector('[data-qty]');
      this.picker = this.querySelector('mm-bundle-picker');
      this.sheet = this.querySelector('[data-sheet]');
      this.hasSheet = !!this.sheet && this.product.options.length === 1 && this.product.variants.length > 1;
      this.setupGallery();
      this.setupSticky();
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('mm:bundle:change', () => this.refresh());
      this.addEventListener('mm:bundle:single', (e) => this.selectVariant(e.detail.id));
      this.setupReels();
      this.qtyInput?.addEventListener('change', () => this.setQty(this.qtyInput.value));
      // Enter in the quantity field submits the product form: same add path, never a native post.
      this.querySelector('[data-product-form]')?.addEventListener('submit', (e) => { e.preventDefault(); this.add(this.querySelector('[data-atc]'), this.querySelector('[data-atc-error]')); });
      this.updateOptions();
      this.updateSizes();
      this.updatePre();
      if (this.dataset.jump === 'true' && this.variant?.featured_media) this.goToMedia(this.variant.featured_media.id, false);
      this.refresh();
    }

    get pickerOn() { return !!this.picker?.active; }

    onClick(e) {
      const t = e.target;
      const opt = t.closest('[data-option-value]');
      if (opt) { if (!opt.disabled) this.selectOption(Number(opt.dataset.optionIndex), opt.dataset.optionValue); return; }
      if (t.closest('[data-qty-minus]')) { this.setQty(this.qty - 1); return; }
      if (t.closest('[data-qty-plus]')) { this.setQty(this.qty + 1); return; }
      const atc = t.closest('[data-atc]');
      if (atc) { this.add(atc, this.querySelector('[data-atc-error]')); return; }
      if (t.closest('[data-bar-dec]')) { this.setBags(this.state().qty - 1); return; }
      if (t.closest('[data-bar-inc]')) { this.setBags(this.state().qty + 1); return; }
      const bar = t.closest('[data-bar-add]');
      if (bar) { this.barAdd(bar); return; }
      if (t.closest('[data-sheet-close]')) { this.closeSheet(); return; }
      const sc = t.closest('[data-sheet-colour]');
      if (sc) { if (!sc.disabled) this.sheetPick(Number(sc.dataset.sheetColour)); return; }
      const ss = t.closest('[data-sheet-slot]');
      if (ss) { this.picker.setSlot(Number(ss.dataset.sheetSlot)); return; }
      const sa = t.closest('[data-sheet-add]');
      if (sa) { this.add(sa, this.querySelector('[data-sheet-error]')); return; }
      const thumb = t.closest('[data-goto]');
      if (thumb) { this.goTo(Number(thumb.dataset.goto)); return; }
      const n = this.slides?.length || 1;
      if (t.closest('[data-gallery-prev]')) { this.goTo((this.index - 1 + n) % n); return; }
      if (t.closest('[data-gallery-next]')) this.goTo((this.index + 1) % n);
    }

    /* Variants */
    findVariant(sel) { return this.product.variants.find((v) => v.options.every((o, i) => o === sel[i])); }
    selectOption(i, value) {
      this.selected[i] = value;
      let v = this.findVariant(this.selected);
      if (!v) {
        v = this.product.variants.find((x) => x.options[i] === value && x.available) || this.product.variants.find((x) => x.options[i] === value);
        if (v) this.selected = [...v.options];
      }
      if (v) this.setVariant(v);
    }
    selectVariant(id) { const v = this.product.variants.find((x) => x.id === id); if (v) { this.selected = [...v.options]; this.setVariant(v); } }
    setVariant(v) {
      this.variant = v;
      if (this.input) this.input.value = v.id;
      this.updateOptions();
      this.updatePrice();
      this.updateSizes();
      this.updatePre();
      if (v.featured_media) this.goToMedia(v.featured_media.id);
      if (!window.Shopify?.designMode) {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState(window.history.state, '', u.toString());
      }
      this.dispatchEvent(new CustomEvent('mm:variant:change', { bubbles: true, detail: { variant: v } }));
      this.refresh();
    }
    updateOptions() {
      this.querySelectorAll('[data-option]').forEach((fs) => {
        const i = Number(fs.dataset.option);
        const label = fs.querySelector('[data-option-label]');
        if (label) label.textContent = clean(this.selected[i]);
        fs.querySelectorAll('[data-option-value]').forEach((b) => {
          const value = b.dataset.optionValue;
          const on = value === this.selected[i];
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-checked', on);
          // Available with the other current choices (single-option products: the value's own stock).
          const ok = this.product.variants.some((v) => v.available && v.options[i] === value && v.options.every((o, k) => k === i || o === this.selected[k]));
          b.disabled = !ok && !on;
          b.classList.toggle('is-soldout', !ok);
        });
      });
    }
    updatePrice() {
      const v = this.variant;
      const box = this.querySelector('[data-price]');
      const fmt = this.dataset.saveFormat || 'amount';
      const sale = v.compare_at_price > v.price;
      if (box) {
        const saved = v.compare_at_price - v.price;
        const code = esc(this.dataset.code || '');
        const save = sale && fmt !== 'none' ? `<span class="mm-pdp__save">${esc(this.dataset.tSave)} ${fmt === 'percent' ? `${Math.floor((saved * 100) / v.compare_at_price)}%` : money(saved)}</span>` : '';
        box.innerHTML = `<span class="mm-pdp__now">${money(v.price)}${code}</span>${sale ? `<s class="mm-pdp__was"><span class="mm-visually-hidden">${esc(this.dataset.tRegular)}</span>${money(v.compare_at_price)}${code}</s>${save}` : ''}`;
      }
      const pill = this.querySelector('[data-sale-badge]');
      if (pill) { const text = sale ? this.dataset.tSale : this.dataset.tNosale; pill.textContent = text || ''; pill.hidden = !text; }
    }
    // Size tiles carry the chosen colour to the sibling size when that colour exists there (?variant=).
    updateSizes() {
      const key = clean(this.variant?.option1 || this.variant?.title).toLowerCase();
      this.querySelectorAll('[data-size-tile]').forEach((a) => {
        if (a.getAttribute('aria-current') === 'page') return;
        let map = {};
        try { map = JSON.parse(a.dataset.colours || '{}'); } catch {}
        const u = new URL(a.dataset.url || a.href, window.location.href);
        if (map[key]) u.searchParams.set('variant', map[key]); else u.searchParams.delete('variant');
        a.href = u.pathname + u.search;
      });
    }
    updatePre() {
      const box = this.querySelector('[data-variant-pre]');
      if (!box) return;
      const v = this.variant;
      const on = isPre(v) && !this.pickerOn;
      box.hidden = !on;
      if (!on) return;
      const ships = preNote(v);
      const shipsText = ships ? ships.replace(/^Ships/i, 'ships') : '';
      const head = shipsText ? fill(this.dataset.tPreHead, { colour: clean(v.title), ships: shipsText }) : fill(this.dataset.tPreHeadPlain, { colour: clean(v.title) });
      box.innerHTML = `<strong>${esc(head)}</strong> ${esc(this.dataset.tPreNote)}`;
    }

    /* Sticky-bar stepper: one count (1 to the picker's max, 10 by default) that drives the same state as the buy box.
       Picker products: bags in the Bundle & Save picker (qty box back to 1); others: the quantity box. The add
       payload is unchanged: plain { id, quantity } lines. */
    get barMax() { return this.pickerOn ? Math.min(Math.max(this.picker.max || 10, 1), 10) : 10; }
    setBags(n) {
      n = Math.max(1, Math.min(Number(n) || 1, this.barMax));
      if (this.pickerOn) {
        this.qty = 1;
        if (this.qtyInput) this.qtyInput.value = 1;
        this.picker.setQty(n); // fires mm:bundle:change, which refreshes the bar
        this.refresh();
      } else this.setQty(n);
    }

    /* Quantity + totals */
    setQty(q) {
      this.qty = Math.max(1, Math.min(Number(q) || 1, 99));
      if (this.qtyInput) this.qtyInput.value = this.qty;
      this.refresh();
    }
    // multi = the Bundle & Save picker drives the add (any tier); qty = all bags (picker bags × quantity stepper).
    state() {
      const v = this.variant;
      const multi = this.pickerOn;
      if (multi) {
        const s = this.picker.summary(this.qty);
        return { multi, qty: s.qty, bags: this.picker.qty, total: s.total, compare: s.compare, available: s.picks.every((x) => x.available), picks: s.picks };
      }
      if (!v) return { multi, qty: 0, bags: 0, total: 0, compare: 0, available: false };
      const q = this.qty;
      const unitCompare = v.compare_at_price > v.price ? v.compare_at_price : v.price;
      return { multi, qty: q, bags: q, total: v.price * q, compare: unitCompare * q, available: v.available };
    }
    refresh() {
      const s = this.state();
      const d = this.dataset;
      const label = !this.variant ? d.tUnavailable : s.available ? `${d.tAdd} · ${money(s.total)}` : d.tSoldout;
      const atc = this.querySelector('[data-atc]');
      if (atc) { atc.textContent = label; atc.disabled = !s.available; }
      this.updatePre();
      // Sticky bar
      const img = this.querySelector('[data-bar-img]');
      const first = s.multi ? s.picks[0] : this.variant;
      const src = first?.featured_image?.src;
      if (img && src) img.src = sized(src, 120);
      const meta = this.querySelector('[data-bar-meta]');
      if (meta) {
        // "SALY Classic · 3 bags": picker products always count bags; others show the colour.
        const text = this.pickerOn ? fill(d.tBarMulti, { count: s.qty, units: s.qty === 1 ? d.unit || '' : d.units || '' }) : clean(this.variant?.title === 'Default Title' ? '' : this.variant?.title);
        meta.textContent = text ? ` · ${text}` : '';
      }
      const count = this.querySelector('[data-bar-count]');
      if (count) count.textContent = s.qty;
      const dec = this.querySelector('[data-bar-dec]');
      const inc = this.querySelector('[data-bar-inc]');
      if (dec) dec.disabled = s.qty <= 1;
      if (inc) inc.disabled = s.qty >= this.barMax;
      const total = this.querySelector('[data-bar-total]');
      if (total) total.textContent = money(s.total);
      const was = this.querySelector('[data-bar-compare]');
      if (was) { was.textContent = s.compare > s.total ? money(s.compare) : ''; was.hidden = !(s.compare > s.total); }
      const barAdd = this.querySelector('[data-bar-add]');
      if (barAdd) { barAdd.disabled = !s.available; barAdd.textContent = s.available ? d.tAdd : d.tSoldout; }
      if (this.sheet && !this.sheet.hidden) this.renderSheet();
    }

    /* Add to cart */
    async add(btn, err) {
      if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
      const s = this.state();
      if (!s.available) return;
      let ok = false;
      if (s.multi) ok = await this.picker.add(btn, err, this.qty);
      else {
        btn.setAttribute('aria-busy', 'true');
        if (err) err.hidden = true;
        try { await window.MM.addItems([{ id: this.variant.id, quantity: this.qty }]); ok = true; }
        catch (e) { if (err) { err.textContent = e.message || this.dataset.tError; err.hidden = false; } }
        finally { btn.removeAttribute('aria-busy'); }
      }
      if (ok) this.closeSheet(true);
    }
    barAdd(btn) {
      const sheetOn = phone() ? this.dataset.sheetPhone === 'true' : this.dataset.sheetDesktop === 'true';
      if (this.hasSheet && sheetOn) { this.openSheet(btn); return; }
      this.add(btn, this.querySelector('[data-bar-error]'));
    }

    /* Phone colour sheet: colour cards for the single colour, or one bag at a time for a 2+ bundle. */
    openSheet(trigger) {
      this.sheetTrigger = trigger;
      this.sheet.hidden = false;
      this.renderSheet();
      document.documentElement.classList.add('mm-lock');
      requestAnimationFrame(() => this.sheet.classList.add('is-open'));
      this.sheet.querySelector('.mm-pdp-sheet__panel')?.focus({ preventScroll: true });
      this.onKey = (e) => {
        if (e.key === 'Escape') this.closeSheet();
        if (e.key === 'Tab') { // keep focus inside the sheet
          const f = [...this.sheet.querySelectorAll('button:not([disabled])')].filter((x) => x.offsetParent);
          if (!f.length) return;
          if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
          else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
        }
      };
      document.addEventListener('keydown', this.onKey);
    }
    closeSheet(added = false) {
      if (!this.sheet || this.sheet.hidden) return;
      this.sheet.classList.remove('is-open');
      this.sheet.hidden = true;
      document.documentElement.classList.remove('mm-lock');
      document.removeEventListener('keydown', this.onKey);
      if (!added) this.sheetTrigger?.focus({ preventScroll: true });
    }
    sheetPick(id) {
      if (this.pickerOn) this.picker.pick(id); // 1 bag: the picker hands it back as the page's variant
      else this.selectVariant(id);
    }
    renderSheet() {
      const body = this.sheet.querySelector('[data-sheet-body]');
      if (!body) return;
      const d = this.dataset;
      const s = this.state();
      const p = this.picker;
      const bg = (v) => E()?.swatchBg?.(v.option1 || v.title) || (v.featured_image?.src ? `url('${sized(v.featured_image.src, 96)}')` : 'var(--mm-sand-200)');
      const activeId = s.multi ? p.picks[p.activeBag] : this.variant?.id;
      const cards = this.product.variants.map((v) => {
        const on = v.id === activeId;
        return `<button type="button" class="mm-pdp-sheet__card" role="radio" aria-checked="${on}" data-sheet-colour="${v.id}"${v.available ? '' : ' disabled'}><span class="mm-pdp-sheet__dot" style="--swatch:${esc(bg(v))}"></span><span class="mm-pdp-sheet__name">${esc(clean(v.title))}${v.available ? '' : ` <span class="mm-visually-hidden">(${esc(d.tSoldout)})</span>`}</span></button>`;
      }).join('');
      const title = this.sheet.querySelector('[data-sheet-title]');
      const multiTitle = s.multi && p.qty > 1 && d.tSheetMulti ? fill(d.tSheetMulti, { count: p.qty, unit: d.unit || '', units: d.units || '' }) : '';
      if (title) title.textContent = multiTitle || d.tSheet;
      body.innerHTML = `<div class="mm-pdp-sheet__cards" role="radiogroup" aria-label="${esc(title?.textContent || '')}">${cards}</div>`;
      const add = this.sheet.querySelector('[data-sheet-add]');
      if (add) { add.textContent = s.available ? `${d.tAdd} · ${money(s.total)}` : d.tSoldout; add.disabled = !s.available; }
    }

    /* Reels: play while on screen (muted, looped), pause when off. */
    setupReels() {
      const vids = [...this.querySelectorAll('.mm-pdp__reel video')];
      if (!vids.length || typeof IntersectionObserver === 'undefined') return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.reelIO = new IntersectionObserver((entries) => entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting && !reduce) { v.muted = true; v.play?.().catch(() => {}); } else v.pause?.();
      }), { threshold: 0.4 });
      vids.forEach((v) => { v.muted = true; v.setAttribute('playsinline', ''); this.reelIO.observe(v); });
    }

    /* Sticky gallery (desktop, beside the buy box): top = min(24px, viewport − gallery height − 24px), so a gallery
       taller than the screen pins by its bottom edge instead of hiding it. */
    setupSticky() {
      const media = this.querySelector('.mm-pdp__media');
      const info = this.querySelector('.mm-pdp__info');
      if (!media) return;
      const measure = () => {
        // Pinned only while gallery and buy box sit side by side (the grid stacks them below 2 × 460px).
        this.classList.toggle('is-two', !!info && Math.abs(info.offsetTop - media.offsetTop) < 4 && info.offsetLeft !== media.offsetLeft);
        // An always-visible (sticky) header sits on top of the pinned photo, so its height joins the offset and the viewport.
        const hh = document.querySelector('mm-header')?.dataset.sticky === 'always' ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mm-header-h')) || 0 : 0;
        const top = Math.min(24, Math.round(window.innerHeight - hh - media.offsetHeight - 24)) + hh;
        media.style.setProperty('--mm-gal-top', `${top}px`);
      };
      measure();
      this.onResizeGal = measure;
      window.addEventListener('resize', measure);
      if (typeof ResizeObserver !== 'undefined') { this.galRO = new ResizeObserver(measure); this.galRO.observe(media); }
    }
    disconnectedCallback() {
      if (this.onResizeGal) window.removeEventListener('resize', this.onResizeGal);
      this.galRO?.disconnect();
      this.reelIO?.disconnect();
    }

    /* Gallery */
    setupGallery() {
      const g = this.querySelector('[data-gallery]');
      this.index = 0;
      if (!g) return;
      this.track = g.querySelector('[data-track]');
      this.slides = [...(this.track?.children || [])];
      this.thumbs = [...g.querySelectorAll('[data-goto]')];
      this.counter = g.querySelector('[data-counter]');
      this.prevBtn = g.querySelector('[data-gallery-prev]');
      this.nextBtn = g.querySelector('[data-gallery-next]');
      if (!this.track) return;
      let raf = 0;
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.onSlide()); }, { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
      });
      this.updateGallery();
    }
    onSlide() {
      const w = this.track.clientWidth || 1;
      const i = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / w)));
      if (i !== this.index) { this.index = i; this.updateGallery(); }
    }
    goTo(i, smooth = true) {
      if (!this.track || !this.slides.length) return;
      const next = Math.max(0, Math.min(this.slides.length - 1, i));
      // Neighbours glide; longer jumps (thumbs, colour changes) land at once instead of scrolling through every photo.
      const glide = smooth && Math.abs(next - this.index) <= 1;
      this.index = next;
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: glide ? 'smooth' : 'auto' });
      this.updateGallery();
    }
    goToMedia(id, smooth = true) {
      const i = (this.slides || []).findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0) this.goTo(i, smooth);
    }
    updateGallery() {
      if (this.counter) this.counter.textContent = this.index + 1;
      this.thumbs.forEach((t, k) => { const on = k === this.index; t.classList.toggle('is-on', on); t.setAttribute('aria-current', on ? 'true' : 'false'); });
      const th = this.thumbs[this.index];
      const strip = th?.parentElement;
      if (th && strip && strip.scrollWidth > strip.clientWidth) strip.scrollTo({ left: th.offsetLeft - (strip.clientWidth - th.offsetWidth) / 2, behavior: 'smooth' });
      this.slides.forEach((s, k) => { if (k !== this.index) s.querySelectorAll('video').forEach((v) => v.pause()); });
    }
  });

  /* Ships-by estimate: <mm-ship-by> lives in assets/mm-global.js (shared with the Trio hero, Home hero and cart). */

  /* ---------------- Recommendations ---------------- */
  define('mm-recommendations', class extends HTMLElement {
    connectedCallback() {
      // Design card blocks: "Add to cart" adds that product's first available variant, [{ id, quantity: 1 }].
      if (!this.bound) {
        this.bound = true;
        this.addEventListener('click', async (e) => {
          const b = e.target.closest('[data-rec-add]');
          if (!b || b.getAttribute('aria-busy') === 'true') return;
          b.setAttribute('aria-busy', 'true');
          try { await window.MM.addItems([{ id: Number(b.dataset.recAdd), quantity: 1 }]); } catch {}
          finally { b.removeAttribute('aria-busy'); }
        });
      }
      if (this.loading || !this.dataset.url || this.querySelector('[data-track] > *')) return;
      this.loading = true;
      (async () => {
        let ok = await this.load(this.dataset.url);
        if (!ok && this.dataset.fallbackUrl) ok = await this.load(this.dataset.fallbackUrl);
        if (!ok && !window.Shopify?.designMode) this.closest('.mm-recommendations')?.setAttribute('hidden', '');
      })();
    }
    async load(url) {
      try {
        const res = await fetch(url);
        if (!res.ok) return false;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('mm-recommendations');
        if (!fresh || !fresh.querySelector('[data-track] > *')) return false;
        this.innerHTML = fresh.innerHTML;
        return true;
      } catch { return false; }
    }
  });

  /* ---------------- Accordions: open the block selected in the theme editor ---------------- */
  define('mm-acc-group', class extends HTMLElement {
    connectedCallback() {
      this.onSelect = (e) => { const el = this.querySelector(`[data-block-id="${e.detail?.blockId}"]`); if (el) el.open = true; };
      document.addEventListener('shopify:block:select', this.onSelect);
      // Global shipping rates (sections/mm-shipping-rates, rendered by the layout as <template id="mm-shipping-rates-tpl">)
      // replace each shipping accordion's local copy ([data-mm-ship-local]); re-mounted when that section reloads in the editor.
      this.mountShipping();
      this.onSectionLoad = () => this.mountShipping();
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      if (this.bound) return;
      this.bound = true;
      // Shipping region pills (sections/mm-pdp-details): one rate table at a time.
      this.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-region]');
        const box = pill?.closest('[data-regions]');
        if (!box) return;
        box.querySelectorAll('[data-region]').forEach((b) => b.setAttribute('aria-selected', String(b === pill)));
        box.querySelectorAll('[data-region-panel]').forEach((p) => { p.hidden = p.dataset.regionPanel !== pill.dataset.region; });
      });
      // Design: one answer open at a time when the group asks for it (data-single).
      if (this.dataset.single === 'true') this.addEventListener('toggle', (e) => {
        const d = e.target;
        if (d.open) this.querySelectorAll('details[open]').forEach((x) => { if (x !== d) x.open = false; });
      }, true);
    }
    mountShipping() {
      const tpl = document.getElementById('mm-shipping-rates-tpl');
      if (!tpl) return;
      this.querySelectorAll('[data-mm-ship-local]').forEach((slot) => {
        const node = tpl.content.firstElementChild?.cloneNode(true);
        if (!node) return;
        node.setAttribute('data-mm-ship-local', '');
        node.setAttribute('data-mm-ship-global', '');
        slot.replaceWith(node);
      });
    }
    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onSelect);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
    }
  });
})();
